/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {
    adminLogin,
    createSubscriptionExistingUser,
    deleteAdminUser,
    deleteUser,
    getAdminUsers,
    getUser,
    register,
    updateAdminUser,
    updatePassword,
    updateUser,
    userHasSubscription
} from '../util/requestSender';

import {Country, UserFilter, UserStatus, UserType} from '../util/interfaces';

import {ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import Account from './accountModel';

import Subscription from './subscriptionModel';

import {hasAdminLogin, setIsAdminLogin} from '../util/adminUtil';

import {setAnalyticsCollectionEnabled} from '../util/analytics';

import {deepClone, getFingerprint} from '../util/utils';

interface Props extends BaseProps {
    last_login?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
    status?: UserStatus;
    flex?: Record<string, any>;
    type?: UserType;
    two_factor_enabled?: number | boolean;
    mail_opt_in?: boolean;
    accounts?: (Account | any)[];
    tel_number?: string;
    address?: string;
    zip_code?: string;
    city?: string;
    country?: string;
    override_limitations?: boolean;
    demo_account?: boolean;
    subscription?: Subscription | any | null;
    is_google_account?: boolean;
    admin_flex?: Record<string, any>;
}

export interface UserFields extends BaseFields {
    last_login: string;
    email: string;
    firstname: string;
    lastname: string;
    status: UserStatus;
    flex: Record<string, any>;
    type: UserType;
    two_factor_enabled: boolean;
    mail_opt_in: boolean;
    accounts: Account[];
    tel_number: string;
    address: string;
    zip_code: string;
    city: string;
    override_limitations: boolean;
    country: Country;
    demo_account: boolean;
    subscription: Subscription | null;
    is_google_account: boolean;
    admin_flex: Record<string, any>;
}

const fields: UserFields = {
    id: '',
    created_at: '',
    updated_at: '',
    last_login: '',
    email: '',
    firstname: '',
    lastname: '',
    status: UserStatus.ACTIVE,
    flex: {},
    type: UserType.USER,
    two_factor_enabled: false,
    mail_opt_in: true,
    accounts: new Array<Account>(),
    tel_number: '',
    address: '',
    zip_code: '',
    city: '',
    override_limitations: false,
    country: Country.NL,
    demo_account: false,
    subscription: null,
    is_google_account: false,
    admin_flex: {}
};

const fieldsDefinition: {[key in keyof Omit<UserFields, 'accounts' | 'subscription'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    last_login: createFieldDef(ModelFieldType.STRING, undefined, true),
    email: createFieldDef(ModelFieldType.STRING),
    firstname: createFieldDef(ModelFieldType.STRING),
    lastname: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, UserStatus.ACTIVE),
    flex: createFieldDef(ModelFieldType.OBJECT),
    type: createFieldDef(ModelFieldType.STRING, UserType.USER),
    two_factor_enabled: createFieldDef(ModelFieldType.BOOLEAN, false, false, false, true),
    mail_opt_in: createFieldDef(ModelFieldType.BOOLEAN, true),
    tel_number: createFieldDef(ModelFieldType.STRING),
    address: createFieldDef(ModelFieldType.STRING),
    zip_code: createFieldDef(ModelFieldType.STRING),
    city: createFieldDef(ModelFieldType.STRING),
    country: createFieldDef(ModelFieldType.STRING, Country.NL),
    demo_account: createFieldDef(ModelFieldType.BOOLEAN, false),
    is_google_account: createFieldDef(ModelFieldType.BOOLEAN),
    override_limitations: createFieldDef(ModelFieldType.BOOLEAN),
    admin_flex: createFieldDef(ModelFieldType.OBJECT)
};

const skipFields: Array<keyof Props> = ['accounts', 'subscription'];

export default class User extends BaseModel<Props, UserFields> {
    static getAdminUsers = (
        limit: number,
        page: number,
        search: string,
        filter: UserFilter | undefined,
        callback: ModelResponse<User[]>
    ) => {
        return getAdminUsers({
            data: {limit, page, search, filter},
            callback: (data: ResponseData) => {
                const users: User[] = data.success ? data.users.map((user: any) => new User(user)) : [];
                callback(users, data.success, data);
            }
        });
    };

    static get = (
        id: string | null,
        callback: ModelResponse<User | null>,
        onError?: (response: Response) => void
    ): void => {
        getUser({
            id,
            callback: (data: ResponseData) => {
                const success = data.success,
                    user = success ? new User(data.user) : null;
                setIsAdminLogin(data.is_admin_login);
                setAnalyticsCollectionEnabled(data.analytics_collection_enabled ?? true);
                callback(user, success, data);
            },
            errorCallback: onError
        });
    };

    constructor(props?: Props) {
        super(skipFields, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get lastLogin(): string {
        return this.fields.last_login;
    }

    set lastLogin(last_login: string) {
        this.fields.last_login = last_login;
    }

    get lastLoginMoment(): IDate {
        return new IDate(this.lastLogin);
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(firstname: string) {
        this.fields.firstname = firstname;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    set lastname(lastname: string) {
        this.fields.lastname = lastname;
    }

    get status(): UserStatus {
        return this.fields.status;
    }

    get translatedStatus(): string {
        switch (this.status) {
            case UserStatus.ACTIVE:
                return 'Actief';
            case UserStatus.INACTIVE:
                return 'Inactief';
            case UserStatus.BLOCKED:
                return 'Geblokkeerd';
            case UserStatus.INCOMPLETE:
                return 'Niet compleet';
        }
    }

    set status(status: UserStatus) {
        this.fields.status = status;
    }

    get flex(): Record<string, any> {
        return this.fields.flex;
    }

    set flex(flex: Record<string, any>) {
        this.fields.flex = flex;
    }

    get adminFlex(): Record<string, any> {
        return this.fields.admin_flex;
    }

    set adminFlex(flex: Record<string, any>) {
        this.fields.admin_flex = flex;
    }

    get type(): UserType {
        return this.fields.type;
    }

    get translatedType(): string {
        switch (this.type) {
            case UserType.ADMIN:
                return 'Admin';
            case UserType.PREMIUM:
                return 'Premium gebruiker';
            case UserType.USER:
                return 'Gebruiker';
            case UserType.SUPER_ADMIN:
                return 'Super admin';
            case UserType.SUPPORT:
                return 'Support';
        }
    }

    set type(type: UserType) {
        this.fields.type = type;
    }

    get twoFactorEnabled(): boolean {
        return this.fields.two_factor_enabled;
    }

    set twoFactorEnabled(enabled: boolean) {
        this.fields.two_factor_enabled = enabled;
    }

    get accounts(): Account[] {
        return this.fields.accounts;
    }

    set accounts(accounts: Account[]) {
        this.fields.accounts = accounts;
    }

    get fullName(): string {
        return this.firstname + (this.lastname.length ? ` ${this.lastname}` : '');
    }

    get mailOptIn(): boolean {
        return this.fields.mail_opt_in;
    }

    set mailOptIn(optIn: boolean) {
        this.fields.mail_opt_in = optIn;
    }

    get telNumber(): string {
        return this.fields.tel_number;
    }

    set telNumber(tel_number: string) {
        this.fields.tel_number = tel_number;
    }

    get address(): string {
        return this.fields.address;
    }

    set address(address: string) {
        this.fields.address = address;
    }

    get zipCode(): string {
        return this.fields.zip_code;
    }

    set zipCode(zip_code: string) {
        this.fields.zip_code = zip_code;
    }

    get city(): string {
        return this.fields.city;
    }

    set city(city: string) {
        this.fields.city = city;
    }

    get country(): Country {
        return this.fields.country;
    }

    set country(country: Country) {
        this.fields.country = country;
    }

    get subscription(): Subscription | null {
        return this.fields.subscription;
    }

    set subscription(subscription: Subscription | null) {
        this.fields.subscription = subscription;
    }

    get isDemoAccount(): boolean {
        return this.fields.demo_account;
    }

    get isGoogleAccount(): boolean {
        return this.fields.is_google_account;
    }

    set isGoogleAccount(is_google_account: boolean) {
        this.fields.is_google_account = is_google_account;
    }

    get overrideLimitations(): boolean {
        return this.fields.override_limitations;
    }

    set overrideLimitations(value: boolean) {
        this.fields.override_limitations = value;
    }

    get isNewUser() {
        return this.getFlex('registration_complete') !== true;
    }

    get isNewAccount() {
        return this.getFlex('is_new_account');
    }

    setAdminFlex = (key: string, value: any) => {
        this.adminFlex = {...this.adminFlex, [key]: value};
    };

    getAdminFlex = (key: string): any | null => {
        return this.adminFlex[key];
    };

    clone = (deep: boolean = true): User => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new User(fields);
    };

    setFlex = (key: string, value: any) => {
        this.flex = {...this.flex, [key]: value};
    };

    getFlex = (key: string): any | null => {
        const {flex} = this;
        if (Object.hasOwn(flex, key)) return flex[key];
        return null;
    };

    addAccount = (account: Account): User => {
        for (const acc of this.accounts) if (acc.id === account.id) return this.updateAccount(account);
        this.accounts = this.accounts.concat(account);
        return this;
    };

    updateAccount = (account: Account): User => this.removeAccount(account).addAccount(account);

    removeAccount = (account: Account): User => {
        this.accounts = this.accounts.filter((acc: Account) => acc.id !== account.id);
        return this;
    };

    register = (
        password: string,
        invitationID: string | null,
        callback: ModelResponse<User>,
        errorCallback?: (response: Response) => void
    ) => {
        getFingerprint().then((fingerprint: string) => {
            register({
                data: {
                    email: this.email,
                    firstname: this.firstname,
                    flex: this.flex,
                    lastname: this.lastname,
                    password,
                    invitation_id: invitationID,
                    fp: fingerprint
                },
                callback: (data: ResponseData) => {
                    if (data.success) this.build(data.user);
                    callback(this, data.success, data);
                },
                // Forward so a blocked/non-2xx register (e.g. a Cloudflare 403
                // after the app sat idle) resets the caller's spinner instead of
                // hanging — same fix as login. See requestSender.register.
                errorCallback
            });
        });
    };

    update = (password?: string, callback?: ModelResponse<User>, updateBilling: boolean = false) => {
        const data = this.createRequestData(updateBilling);
        if (password) data.password = password;
        updateUser({
            data,
            callback: (data: ResponseData) => {
                const success = data.success;
                if (success) this.build(data.user);
                if (callback) callback(this, success, data);
            }
        });
    };

    changePassword = (password: string, newPassword: string, callback: ModelResponse<User>) => {
        updatePassword({
            data: {password, new_password: newPassword, activation_key: null},
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.user);
                callback(this, data.success, data);
            }
        });
    };

    updateAdmin = (callback: ModelResponse<User>) => {
        const data = {
            email: this.email,
            firstname: this.firstname,
            lastname: this.lastname,
            flex: this.flex,
            type: this.type,
            status: this.status,
            tel_number: nullify(this.telNumber),
            address: nullify(this.address),
            zip_code: nullify(this.zipCode),
            city: nullify(this.city),
            country: nullify(this.country),
            subscription_status: this.subscription?.status,
            override_limitations: this.overrideLimitations,
            admin_flex: this.adminFlex
        };
        updateAdminUser({
            id: this.id,
            data,
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.user);
                callback(this, data.success, data);
            }
        });
    };

    delete = (callback: (success: boolean, data: ResponseData) => void) => {
        deleteUser({
            id: this.id,
            callback: (data: ResponseData) => {
                callback(!!data.success, data);
            }
        });
    };

    deleteAdmin = (callback: ModelResponse<User>) => {
        deleteAdminUser({
            id: this.id,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    adminLogin = (callback: ModelResponse<User | null>) => {
        return adminLogin({
            id: this.id,
            callback: (data: ResponseData) => {
                const user: User | null = data.success ? new User(data.user) : null;
                callback(user, data.success, data);
            }
        });
    };

    hasSubscription = (callback: ModelResponse<boolean>) => {
        return userHasSubscription({
            callback: (data: ResponseData) => {
                callback(data.has_subscription, data.success, data);
            }
        });
    };

    createSubscription = (callback: ModelResponse<Subscription | null>) => {
        createSubscriptionExistingUser({
            data: {
                address: this.address,
                zip_code: this.zipCode,
                city: this.city,
                country: this.country
            },
            callback: (data: ResponseData) => {
                const subscription = data.success ? new Subscription(data.subscription) : null;
                callback(subscription, data.success, data);
            }
        });
    };

    isSupport = (): boolean => [UserType.ADMIN, UserType.SUPPORT, UserType.SUPER_ADMIN].includes(this.type);

    isAdmin = (): boolean => {
        return [UserType.ADMIN, UserType.SUPER_ADMIN].includes(this.type) || hasAdminLogin();
    };

    isSuperAdmin = (): boolean => {
        return this.type === UserType.SUPER_ADMIN;
    };

    isSubscriptionOwner = (): boolean => {
        if (!this.subscription) return false;
        return this.subscription.userId === this.id;
    };

    hasFullAddress = (): boolean => {
        return !!this.address && !!this.zipCode && !!this.city && !!this.country;
    };

    private createRequestData = (updateBilling: boolean = false) => ({
        email: this.email,
        firstname: this.firstname,
        lastname: this.lastname,
        flex: this.flex,
        group: '',
        password: '',
        tel_number: this.telNumber,
        address: this.address,
        zip_code: this.zipCode,
        city: this.city,
        country: nullify(this.country),
        update_billing: updateBilling
    });

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (typeof props.two_factor_enabled === 'number') this.twoFactorEnabled = props.two_factor_enabled === 1;
            if (props.accounts) {
                const accounts: Account[] = [];
                for (const acc of props.accounts) {
                    const account: Account = acc instanceof Account ? acc : new Account(acc);
                    account.user = this;
                    accounts.push(account);
                }
                this.accounts = accounts;
            }
            if (props.subscription) {
                this.subscription =
                    props.subscription instanceof Subscription
                        ? props.subscription
                        : new Subscription(props.subscription);
            }
        }
    };
}
